<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderItemPrintIndicators.vue
  // (UBadge → PosUiBadge, native title tooltips, no dark: variants).
  import { computed, getCurrentInstance } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';

  type TIndicatorColor = 'success' | 'warning' | 'neutral';

  const props = withDefaults(
    defineProps<{
      quantity: number;
      printC?: number;
      printE?: number;
      isCanceled?: boolean;
      showEtiqueta?: boolean;
    }>(),
    {
      printC: 0,
      printE: 0,
      isCanceled: false,
      showEtiqueta: false,
    }
  );

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  function clamp(val: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, val));
  }

  const quantitySafe = computed(() => Math.max(0, Number(props.quantity || 0)));

  const comandaPrinted = computed(() =>
    clamp(Number(props.printC ?? 0), 0, quantitySafe.value)
  );

  const etiquetaPrinted = computed(() =>
    clamp(Number(props.printE ?? 0), 0, quantitySafe.value)
  );

  function getColor(printed: number): TIndicatorColor {
    if (quantitySafe.value <= 0) return 'neutral';
    if (printed >= quantitySafe.value) return 'success';
    if (printed > 0) return 'warning';
    return 'neutral';
  }

  const comandaColor = computed(() => getColor(comandaPrinted.value));
  const etiquetaColor = computed(() => getColor(etiquetaPrinted.value));

  const comandaLabel = computed(
    () => `C ${comandaPrinted.value}/${quantitySafe.value}`
  );
  const etiquetaLabel = computed(
    () => `E ${etiquetaPrinted.value}/${quantitySafe.value}`
  );

  const comandaTooltip = computed(() =>
    i18nT('posV2.printComandaTooltip', {
      printed: comandaPrinted.value,
      total: quantitySafe.value,
    })
  );
  const etiquetaTooltip = computed(() =>
    i18nT('posV2.printEtiquetaTooltip', {
      printed: etiquetaPrinted.value,
      total: quantitySafe.value,
    })
  );
</script>

<template>
  <div class="pos-v2-print-indicators" :class="{ 'is-canceled': isCanceled }">
    <span :title="comandaTooltip">
      <PosUiBadge
        :label="comandaLabel"
        :color="comandaColor"
        icon="printer"
        variant="soft"
        size="xs"
        class="print-badge"
      />
    </span>

    <span v-if="showEtiqueta" :title="etiquetaTooltip">
      <PosUiBadge
        :label="etiquetaLabel"
        :color="etiquetaColor"
        icon="printer"
        variant="soft"
        size="xs"
        class="print-badge"
      />
    </span>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-print-indicators {
    @apply tw-flex tw-items-center tw-justify-end tw-gap-1;

    &.is-canceled {
      @apply tw-opacity-60;
    }

    .print-badge {
      @apply tw-tracking-tight;
    }
  }
</style>
